import React, { useRef, useEffect } from "react";
import { Plus, Upload, X, Info } from "lucide-react";
import { CHAR_LIMITS, TC_RADIUS } from "../../constants/templateCreatorConstants";
import {
  validateHeaderText,
  detectVariablesInText,
} from "../../utils/templateCreatorValidation";
import FormFieldError from "./FormFieldError";

const HeaderEditor = ({
  mediaSample,
  headerText,
  headerVariables,
  variableType,
  languageDisplay = "English",
  onHeaderTextChange,
  onHeaderVariablesChange,
  headerFile,
  headerFilePreview,
  onFileUpload,
  onFileRemove,
}) => {
  const inputRef = useRef(null);

  useEffect(() => {
    if (mediaSample !== "none") return;
    const detected = detectVariablesInText(headerText, variableType);
    const existingMap = {};
    headerVariables.forEach((v) => {
      existingMap[String(v.name)] = v.sample;
    });
    const newVars = detected.slice(0, 1).map((name) => ({
      name: String(name),
      sample: existingMap[String(name)] || "",
    }));
    const changed =
      newVars.length !== headerVariables.length ||
      newVars.some(
        (v, i) =>
          v.name !== headerVariables[i]?.name ||
          v.sample !== headerVariables[i]?.sample
      );
    if (changed) onHeaderVariablesChange(newVars);
  }, [headerText, variableType, mediaSample]);

  const error = validateHeaderText(headerText, headerVariables);

  const handleAddVariable = () => {
    if (headerVariables.length >= 1) return;
    const el = inputRef.current;
    const start = el ? el.selectionStart : headerText.length;
    const varText = variableType === "named" ? "{{header_param}}" : "{{1}}";
    onHeaderTextChange(
      headerText.substring(0, start) + varText + headerText.substring(start)
    );
  };

  const handleSampleChange = (varName, newSample) => {
    onHeaderVariablesChange(
      headerVariables.map((v) =>
        v.name === varName ? { ...v, sample: newSample } : v
      )
    );
  };

  if (mediaSample !== "none") {
    const labels = {
      image: {
        accept: ".jpg,.jpeg,.png",
        label: "image",
        hint: "JPG, JPEG, PNG — Max 5 MB",
      },
      video: {
        accept: ".mp4,.3gp",
        label: "video",
        hint: "MP4, 3GP — Max 16 MB",
      },
      document: {
        accept: ".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt",
        label: "document",
        hint: "PDF, DOC, DOCX, PPT, PPTX, XLS, XLSX, TXT — Max 100 MB",
      },
      location: { accept: "", label: "location", hint: "Location header" },
    };
    const cfg = labels[mediaSample] || labels.image;

    if (mediaSample === "location") {
      return (
        <p className="text-[12px] text-[#65676b] italic py-2">
          Location header will be set when sending the template.
        </p>
      );
    }

    return (
      <div>
        {!headerFilePreview ? (
          <label className="block cursor-pointer">
            <div className={`border-2 border-dashed border-[#ccd0d5] p-4 text-center hover:border-[#1877f2] transition-colors bg-white ${TC_RADIUS}`}>
              <Upload className="mx-auto mb-1 text-[#65676b]" size={22} />
              <p className="text-[13px] font-medium text-[#050505]">
                Click to upload {cfg.label}
              </p>
              <p className="text-[12px] text-[#65676b]">{cfg.hint}</p>
            </div>
            <input
              type="file"
              className="hidden"
              accept={cfg.accept}
              onChange={onFileUpload}
            />
          </label>
        ) : (
          <div className="relative inline-block">
            {mediaSample === "image" && (
              <img
                src={headerFilePreview}
                alt="Preview"
                className={`max-w-[200px] max-h-32 border border-[#ccd0d5] ${TC_RADIUS}`}
              />
            )}
            {mediaSample === "video" && (
              <div className={`p-3 bg-[#f0f2f5] border border-[#ccd0d5] text-[13px] text-[#65676b] ${TC_RADIUS}`}>
                Video selected
              </div>
            )}
            {mediaSample === "document" && (
              <div className={`p-3 bg-[#f0f2f5] border border-[#ccd0d5] text-[13px] text-[#65676b] ${TC_RADIUS}`}>
                Document selected
              </div>
            )}
            <button
              onClick={onFileRemove}
              className="absolute -top-1.5 -right-1.5 p-0.5 bg-red-500 text-white rounded-full hover:bg-red-600 shadow"
            >
              <X size={12} />
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={headerText}
          onChange={(e) => onHeaderTextChange(e.target.value)}
          placeholder={`Add a short line of text to the header of your message in ${languageDisplay}`}
          maxLength={CHAR_LIMITS.HEADER_TEXT}
          className={`w-full px-3 pt-2 pb-7 text-[15px] border focus:outline-none transition-colors bg-white ${TC_RADIUS} ${
            error
              ? "border-red-300"
              : "border-[#ccd0d5] focus:border-[#1877f2]"
          }`}
        />
        <span className="absolute bottom-2 right-3 text-[12px] text-[#65676b] tabular-nums pointer-events-none">
          {headerText.length}/{CHAR_LIMITS.HEADER_TEXT}
        </span>
      </div>

      <FormFieldError message={error} reserve={false} />

      <div className="flex items-center justify-end mt-1">
        <button
          type="button"
          onClick={handleAddVariable}
          disabled={headerVariables.length >= 1}
          className="flex items-center gap-0.5 text-[13px] font-semibold text-[#1877f2] hover:underline disabled:opacity-40 disabled:cursor-not-allowed disabled:no-underline"
        >
          <Plus size={14} strokeWidth={2.5} />
          Add variable
        </button>
        <button type="button" className="p-0.5 text-[#65676b]" title="Info">
          <Info size={16} />
        </button>
      </div>

      {headerVariables.length > 0 && (
        <div className={`mt-2 flex items-center gap-2 bg-[#f0f2f5] p-2 border border-[#dddfe2] ${TC_RADIUS}`}>
          <span className={`text-[12px] font-mono text-[#65676b] bg-white px-1.5 py-0.5 border border-[#ccd0d5] shrink-0 ${TC_RADIUS}`}>
            {`{{${headerVariables[0].name}}}`}
          </span>
          <input
            type="text"
            value={headerVariables[0].sample}
            onChange={(e) =>
              handleSampleChange(headerVariables[0].name, e.target.value)
            }
            placeholder="Enter sample value"
            className={`flex-1 px-2.5 py-1.5 text-[13px] border border-[#ccd0d5] focus:outline-none focus:border-[#1877f2] bg-white ${TC_RADIUS}`}
          />
        </div>
      )}
    </div>
  );
};

export default HeaderEditor;
